import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { TFunction } from 'i18next';
import { use, useMemo } from 'react';

import { HTTP_STATUS } from '@/api/util/ErrorCode.model.ts';
import CONFIG from '@/config/config.ts';
import { QueryClientMetaModel } from '@/config/QueryClientMeta.model.ts';
import { DataOperationsErrorContext } from '@/errorHandling/DataOperationsErrorContext.ts';
import { captureAppException } from '@/monitoring/sentry.util.ts';
import { PATHS_WITHOUT_SESSION } from '@/routes/RoutesNotRequiringAuth.util.ts';
import { LocalStorageKey, localStorageUtil } from '@/utils/localStorage/localStorage.util';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

export const useGetQueryClient = (): QueryClient => {
  const { t } = useTranslationWithPrefix('generalError');
  const { handleMinorError, handleOperationSuccess } = use(DataOperationsErrorContext);
  return useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        mutations: {
          retry: false,
        },
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
        },
      },
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          captureAppException(error, { contextName: 'react-query-mutation' });
          if (handle401(error)) {
            return;
          }
          if (mutation?.options?.meta?.disableErrorAlert) {
            return;
          }
          handleError(error, handleMinorError, t);
        },
        onSuccess: (_data, _variables, _context, mutation) => {
          if (!(mutation?.options?.meta as Partial<QueryClientMetaModel>)?.disableSuccessAlert) {
            handleOperationSuccess();
          }
        },
      }),
      queryCache: new QueryCache({
        onError: (error: unknown) => {
          captureAppException(error, { contextName: 'react-query-query' });
          console.debug('error');
          if (handle401(error)) {
            return;
          }
          handleError(error, handleMinorError, t);
        },
        onSuccess: (_data, query) => {
          if ((query?.options?.meta as Partial<QueryClientMetaModel>)?.enableSuccessAlert) {
            handleOperationSuccess();
          }
        },
      }),
    });
  }, []);
};

const handleError = (error: unknown, handleMinorError: (error: string) => void, t: TFunction) => {
  if (!error) {
    handleMinorError('Unknown Error');
    return;
  }
  const axiosError = (error as AxiosError).message;
  if (axiosError) {
    return handleMinorError(axiosError);
  }
  return handleMinorError(t('default'));
};

export const LOGIN_REDIRECT_URL = CONFIG.HOST + '/' + CONFIG.LOGIN_REDIRECT_URL;

const handle401 = (error: unknown): boolean => {
  if ((error as AxiosError)?.response?.status === HTTP_STATUS.UNAUTHORIZED) {
    if (PATHS_WITHOUT_SESSION) {
      return false;
    }
    localStorageUtil.setItem(LocalStorageKey.LAST_VISITED_PAGE, location.href);
    globalThis.location.href = LOGIN_REDIRECT_URL;
    return true;
  }
  return false;
};
